import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { changePassword } from '../redux/actions/userActions';

export default function ChangePassword({ selectedUserId }) {
  const dispatch = useDispatch();
  const { loading, error, success } = useSelector((state) => state.changePassword);
  
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleSubmit = () => {
    if (password.trim()) {
      setConfirmDialogOpen(true);
    }
  };

  const handleConfirmChange = async () => {
    setConfirmDialogOpen(false);
    const result = await dispatch(changePassword({ userId: selectedUserId, password }));
    if (result.success) {
      setPassword('');
    }
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, p: { xs: 2, sm: 4 }, mt: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Change Password
      </Typography>

      <Paper sx={{ p: 3, border: '1px solid #e5e7eb' }}>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
          Enter a new password for the selected user. This action cannot be undone.
        </Typography>

        <TextField
          fullWidth
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          disabled={loading}
          sx={{ mb: 3 }}
          placeholder="Enter new password"
          helperText={error || ''}
          error={!!error}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleTogglePasswordVisibility}
                  disabled={loading}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!password.trim() || loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Change Password'}
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Password Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the password for this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmChange} 
            variant="contained"
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm Change'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
